<template>
  <div class="flex flex-col h-full">
    <div class="w-full sm:max-w-md mx-auto flex flex-col h-full min-h-0">
      <div class="flex-shrink-0">
        <StepHeader
            title="Kontaktdaten angeben"
            :currentStep="currentStep"
            :totalSteps="totalSteps"
          />
      </div>

      <!-- Form - Scrollable Container -->
      <form @submit.prevent="submitForm" class="flex flex-col space-y-4 overflow-y-visible sm:overflow-y-auto sm:pr-4 sm:mr-2 h-auto sm:h-[500px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
      <!-- Name Fields -->
      <div class="flex flex-col sm:flex-row gap-4">
        <FormField
          v-model="form.first_name"
          label="Vorname *"
          :error="errors.first_name"
          :touched="touched.first_name"
          autocomplete="given-name"
          @blur="touched.first_name = true"
          class="w-full sm:w-1/2"
        />
        <FormField
          v-model="form.last_name"
          label="Nachname *"
          :error="errors.last_name"
          :touched="touched.last_name"
          autocomplete="family-name"
          @blur="touched.last_name = true"
          class="w-full sm:w-1/2"
        />
      </div>

      <!-- Email Field -->
      <FormField
        v-model="form.email"
        label="E-Mail *"
        type="email"
        :error="errors.email"
        :touched="touched.email"
        autocomplete="email"
        @blur="touched.email = true"
      />

      <!-- Custom Fields -->
      <FormField
        v-for="field in customFields"
        :key="field.field_key"
        v-model="form.customFields[field.field_key]"
        :label="field.label + (field.is_required ? ' *' : '')"
        :type="field.field_type === 'phone' ? 'tel' : field.field_type"
        :placeholder="FIELD_GUIDANCE[field.field_key]?.placeholder"
        :hint="FIELD_GUIDANCE[field.field_key]?.hint"
        :error="errors.customFields?.[field.field_key]"
        :touched="touched.customFields?.[field.field_key]"
        :autocomplete="field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'off'"
        :fieldKey="field.field_key"
        :defaultCountryCode="therapistCountry"
        @blur="touched.customFields[field.field_key] = true"
      />

      <!-- Notes Field -->
      <FormField
        v-model="form.notes"
        label="Anmerkungen"
        type="textarea"
        autocomplete="off"
      />

      <!-- Info Text -->
      <p class="text-xs text-pretty text-gray-500 mb-5 text-center">
        Alle Daten werden sicher verschlüsselt. Mit dem Fortfahren werden unsere
        <a href="https://www.therapsy.de/agb" class="text-primary underline" target="_blank" rel="noopener noreferrer">Nutzungsbedingungen</a> und
        <a href="https://www.therapsy.de/datenschutzerklaerungen/bookings" class="text-primary underline" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a> akzeptiert.
      </p>

      <!-- Submit Button -->
      <div class="w-full">
        <button
          type="submit"
          :disabled="!isFormValid"
          :class="[
            'w-full px-4 py-3 rounded-xl font-medium transition-all hover:cursor-pointer',
            isFormValid
              ? 'bg-primary text-white hover:bg-primary/90 shadow-sm shadow-primary/30'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed',
          ]"
        >
          Termin anfragen
        </button>
      </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import StepHeader from '../StepHeader.vue'
import FormField from './FormField.vue'
import { dataService } from '../../services/data'


const emit = defineEmits(['submit'])

const props = defineProps({
  meetingType: Object,
  dateTime:  {
    type: [String, Object], 
    required: true,
  },
  currentStep: {
    type: Number,
    default: 3,
  },
  totalSteps: {
    type: Number,
    default: 3,
  },
  therapistCountry: {
    type: String,
    default: null,
  }
})

// Input guidance for the well-known field keys TheraPsy's meeting-type editor
// writes (same canonical-key vocabulary as the telefonnummer special case).
// The therapist app imports these answers into the client profile, so the
// formats matter: dates and addresses in these shapes are split into the
// profile's own fields, and the Kürzel only matches an existing client when it
// is exactly the code the practice handed out — a guessed one creates a
// duplicate profile. No example for the Kürzel on purpose: an example invites
// inventing one.
const FIELD_GUIDANCE = {
  geburtsdatum: { placeholder: 'TT.MM.JJJJ' },
  adresse: { placeholder: 'Straße Hausnummer, PLZ Ort' },
  kuerzel: { hint: 'Bitte genau so eingeben, wie Sie es von Ihrer Praxis erhalten haben.' },
}

const customFields = ref([])

const form = ref({
  first_name: '',
  last_name: '',
  email: '',
  notes: '',
  phone: null,
  code_name: null,
  customFields: {}
})

const touched = ref({
  first_name: false,
  last_name: false,
  email: false,
  customFields: {}
})

onMounted(() => {
  // Load custom fields for this meeting type
  if (props.meetingType?.id) {
    customFields.value = dataService.getCustomFields(props.meetingType.id)
    
    // Initialize form and touched state for custom fields
    customFields.value.forEach(field => {
      form.value.customFields[field.field_key] = ''
      touched.value.customFields[field.field_key] = false
    })
  }
})

const errors = computed(() => {
  const baseErrors = {
    first_name:
      !form.value.first_name.trim()
        ? 'Vorname ist erforderlich.'
        : form.value.first_name.length < 2
        ? 'Vorname ist zu kurz.'
        : '',
    last_name:
      !form.value.last_name.trim()
        ? 'Nachname ist erforderlich.'
        : form.value.last_name.length < 2
        ? 'Nachname ist zu kurz.'
        : '',
    email:
      !form.value.email.trim()
        ? 'E-Mail ist erforderlich.'
        : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)
        ? 'Ungültige E-Mail-Adresse.'
        : '',
    customFields: {}
  }

  // Validate custom fields
  customFields.value.forEach(field => {
    const value = form.value.customFields[field.field_key] || ''
    
    if (field.is_required && !value.trim()) {
      baseErrors.customFields[field.field_key] = `${field.label} ist erforderlich.`
    } else if (value && field.field_type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      baseErrors.customFields[field.field_key] = 'Ungültige E-Mail-Adresse.'
    } else if (value && field.field_type === 'phone' && !/^[\d\s\+\-\(\)]+$/.test(value)) {
      baseErrors.customFields[field.field_key] = 'Ungültige Telefonnummer.'
    } else {
      baseErrors.customFields[field.field_key] = ''
    }
  })

  return baseErrors
})

const isFormValid = computed(() => {
  const baseFieldsValid = errors.value.first_name === '' && 
                          errors.value.last_name === '' && 
                          errors.value.email === ''
  const customFieldsValid = Object.values(errors.value.customFields).every(e => e === '')
  return baseFieldsValid && customFieldsValid
})

function submitForm() {
  if (!isFormValid.value) return

  // Build custom fields data separately
  const customData = {}
  customFields.value.forEach(field => {
    const value = form.value.customFields[field.field_key]
    if (value && value.trim()) {
      customData[field.field_key] = value.trim()
    }
  })

  // Prepare submission data with separate clientData and customData
  const submissionData = {
    clientData: {
      first_name: form.value.first_name.trim(),
      last_name: form.value.last_name.trim(),
      email: form.value.email.trim(),
      phone: undefined,
      code_name: undefined
    },
    notes: form.value.notes.trim() || undefined
  }

  // Only include customData if there are custom fields
  if (Object.keys(customData).length > 0) {
    submissionData.customData = customData
  }

  emit('submit', submissionData)
}
</script>
